import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check if token is expired (30 days)
    const isTokenExpired = (tokenDate) => {
        if (!tokenDate) return true;
        const expirationTime = new Date(tokenDate).getTime() + (30 * 24 * 60 * 60 * 1000); // 30 days
        return Date.now() > expirationTime;
    };

    useEffect(() => {
        const token = localStorage.getItem('f4c_web_token');
        const userData = localStorage.getItem('f4c_web_user');
        const tokenDate = localStorage.getItem('f4c_web_token_date');

        if (token && userData) {
            // Check if token is expired
            if (isTokenExpired(tokenDate)) {
                console.log('Token expired, clearing storage');
                localStorage.removeItem('f4c_web_token');
                localStorage.removeItem('f4c_web_refresh_token');
                localStorage.removeItem('f4c_web_user');
                localStorage.removeItem('f4c_web_token_date');
                setLoading(false);
                return;
            }

            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
                setIsAuthenticated(true);
                console.log('User restored from localStorage:', parsedUser.email);
            } catch (error) {
                console.error('Error parsing user data:', error);
                localStorage.removeItem('f4c_web_token');
                localStorage.removeItem('f4c_web_refresh_token');
                localStorage.removeItem('f4c_web_user');
                localStorage.removeItem('f4c_web_token_date');
            }
        }
        setLoading(false);
    }, []);

    const login = async (email, otp, deliveryMethod = 'email', contactId = null, bypassTermsOfService = false) => {
        try {
            // Get contact_id if not provided
            let actualContactId = contactId;
            if (!actualContactId) {
                const deliveryResponse = await authAPI.getOTPDeliveryOptions(email);
                actualContactId = deliveryResponse.data.contact_id;
            }

            // Verify OTP
            const response = await authAPI.verifyOTP(actualContactId, otp, deliveryMethod);
            const { success, message, contact_id, access_token, refresh_token } = response.data;

            if (success && access_token) {
                // Decode basic user info from JWT token (first_name, last_name are in the token)
                // For now, we'll just use the email and contact_id
                // If bypassTermsOfService is true (dev mode), always set accepted_terms_of_service to true
                const userData = {
                    id: contact_id,
                    email: email,
                    name: email, // Will be updated from token payload if needed
                    contact_id: contact_id,
                    accepted_terms_of_service: bypassTermsOfService ? true : (response.data.accepted_terms_of_service || false),
                    ic_contact_guid: response.data.ic_contact_guid || null,
                };

                // Store the JWT tokens, user data, and token creation date
                localStorage.setItem('f4c_web_token', access_token);
                if (refresh_token) {
                    localStorage.setItem('f4c_web_refresh_token', refresh_token);
                }
                localStorage.setItem('f4c_web_user', JSON.stringify(userData));
                localStorage.setItem('f4c_web_token_date', new Date().toISOString());

                setUser(userData);
                setIsAuthenticated(true);

                console.log('Login successful, tokens stored');
                if (bypassTermsOfService) {
                    console.log('Terms of Service bypassed (dev mode)');
                }
                return { success: true, user: userData, accepted_terms_of_service: userData.accepted_terms_of_service };
            } else {
                return {
                    success: false,
                    error: message || 'OTP verification failed'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Login failed'
            };
        }
    };

    const getDeliveryOptions = async (email) => {
        try {
            // Use the new secure endpoint that returns only masked contact info
            const response = await authAPI.getOTPDeliveryOptions(email);

            return {
                success: true,
                data: {
                    contact_id: response.data.contact_id,
                    email: response.data.email,
                    available_options: response.data.available_options
                }
            };
        } catch (error) {
            console.error('Delivery options error:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Contact not found or failed to get delivery options'
            };
        }
    };

    const requestOTP = async (email, deliveryMethod = 'email', contactId = null) => {
        try {
            // Get contact_id if not provided
            let actualContactId = contactId;
            if (!actualContactId) {
                const deliveryResponse = await authAPI.getOTPDeliveryOptions(email);
                actualContactId = deliveryResponse.data.contact_id;
            }

            await authAPI.generateOTP(actualContactId, deliveryMethod, 15);
            return { success: true };
        } catch (error) {
            console.error('OTP request error:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to send OTP'
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('f4c_web_token');
        localStorage.removeItem('f4c_web_refresh_token');
        localStorage.removeItem('f4c_web_user');
        localStorage.removeItem('f4c_web_token_date');
        setUser(null);
        setIsAuthenticated(false);
        console.log('User logged out, localStorage cleared');
    };

    const updateTermsAcceptance = (accepted) => {
        if (user) {
            const updatedUser = { ...user, accepted_terms_of_service: accepted };
            setUser(updatedUser);
            localStorage.setItem('f4c_web_user', JSON.stringify(updatedUser));
        }
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        getDeliveryOptions,
        requestOTP,
        logout,
        updateTermsAcceptance,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

